<template>
  <div class="bg-white rounded-lg border border-gray-200 p-4">
    <div class="text-xs font-semibold text-gray-600 mb-1">{{ label }}</div>
    <div class="text-xs text-gray-400 mb-2 leading-snug">{{ description }}</div>

    <!-- own spinner -->
    <div v-if="loading" class="flex items-center py-1">
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-dark"></div>
    </div>

    <!-- own error, own retry -->
    <div v-else-if="error" class="text-xs text-red-500">
      Couldn't load.
      <button type="button" class="underline hover:no-underline" @click="fetchData">Retry</button>
    </div>

    <!-- answer -->
    <template v-else>
      <div class="text-sm font-medium text-gray-800">
        <span v-if="mostRecentDate">{{ formatDate(mostRecentDate) }}</span>
        <span v-else :class="statusTextClass">{{ noDateText }}</span>
      </div>
      <div class="mt-1 text-xs font-medium" :class="statusTextClass">
        {{ ageLabel }}
      </div>
      <div v-if="statusNote" class="mt-0.5 text-xs text-gray-400">
        {{ statusNote }}
      </div>

      <!-- Per-contract breakout. The tenant figure above is a MAX() across
           contracts, so one fresh contract hides every stale one. Only shown
           when a contract is actually behind: on a clean node this adds
           nothing to the card. -->
      <div v-if="staleContracts.length" class="mt-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          class="text-xs font-medium text-yellow-600 hover:underline"
          @click="expanded = !expanded"
        >
          {{ staleContracts.length }} of {{ contractCount }} contracts behind
          <span class="text-gray-400">{{ expanded ? '▾' : '▸' }}</span>
        </button>
        <ul v-if="expanded" class="mt-1.5 space-y-1">
          <li
            v-for="c in staleContracts"
            :key="c.contract_id"
            class="flex items-baseline justify-between gap-2 text-xs"
          >
            <span class="sensitive text-gray-600 truncate" data-spoof-kind="contract">{{ c.contract_id }}</span>
            <span class="shrink-0 font-medium text-yellow-600">{{ c.age_days }}d</span>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

/**
 * One data-type freshness question, answered independently.
 * Mounts, fires its OWN /api/data-freshness/?node=<key> request, and owns its
 * own loading/error/retry. A slow node (e.g. ADT) only spins this card.
 *
 * The FRESH/STALE/FAILED/NO DATA verdict comes from the API, not from here.
 * This card previously applied a uniform 7d-green / 21d-amber rule client-side,
 * which was wrong for most nodes: CMS claims run 30-60 days behind by design,
 * so a healthy Claims card was amber every day, while ADT (which should arrive
 * same-day) still read green at 5 days stale. Thresholds are per-node and live
 * in data_freshness_service so the cards, the API and the digest cannot drift.
 */
const STATUS_CLASS = {
  FRESH: 'text-green-600',
  STALE: 'text-yellow-600',
  FAILED: 'text-red-500',
  'NO DATA': 'text-gray-400',
};

export default {
  name: 'FreshnessCard',
  props: {
    nodeKey: { type: String, required: true },
    label: { type: String, required: true },
    description: { type: String, default: '' },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      loading: false,
      error: false,
      mostRecentDate: null,
      status: null,
      ageDays: null,
      limitDays: null,
      contracts: [],
      contractCount: 0,
      expanded: false,
    };
  },
  computed: {
    statusTextClass() {
      // A node whose contracts are behind must not read plain-green. The
      // tenant figure is a MAX() across contracts, so FRESH here means "some
      // contract is current", not "all are" -- which is exactly how 4 Vytalize
      // rosters sat 91 days stale under a green "0 days ago". Downgrade the
      // colour to match the worst contract; the verdict word itself still comes
      // from the API.
      if (this.status === 'FRESH' && this.staleContracts.length) {
        return STATUS_CLASS.STALE;
      }
      return STATUS_CLASS[this.status] || 'text-gray-400';
    },
    // Already ranked stalest-first by the API.
    staleContracts() {
      return this.contracts.filter((c) => c.status !== 'FRESH');
    },
    // NO DATA means the tenant has never sent this type — a configuration
    // fact, not an outage. FAILED means rows exist but none inside the
    // service's 120-day window, which is a real gap worth alarming on.
    noDateText() {
      return this.status === 'FAILED' ? 'No recent data' : 'Not sent';
    },
    ageLabel() {
      if (this.ageDays === null || this.ageDays === undefined) {
        return this.status === 'FAILED' ? 'Nothing in the last 120 days' : 'Never received';
      }
      if (this.ageDays === 0) return 'Today';
      if (this.ageDays === 1) return '1 day ago';
      return `${this.ageDays} days ago`;
    },
    // Only say something when the number alone would mislead: an amber/red
    // card should explain what it was measured against.
    statusNote() {
      if (this.status === 'STALE' && this.limitDays !== null) {
        return `Expected within ${this.limitDays} days`;
      }
      if (this.status === 'NO DATA') {
        return 'This tenant does not send this data type';
      }
      return '';
    },
  },
  watch: {
    entityId() { this.fetchData(); },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = false;
      try {
        const params = { node: this.nodeKey };
        if (this.entityId) params.entity_id = this.entityId;
        const resp = await axios.get('api/data-freshness/', { params });
        this.mostRecentDate = resp.data?.most_recent_date ?? null;
        this.status = resp.data?.status ?? null;
        this.ageDays = resp.data?.age_days ?? null;
        this.limitDays = resp.data?.limit_days ?? null;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
      // Fired AFTER the node answer and deliberately not awaited into the
      // card's own loading state: the tenant verdict must render immediately,
      // and a contract breakout that fails or is unsupported for this node
      // should cost the card nothing.
      this.fetchContracts();
    },
    async fetchContracts() {
      try {
        const resp = await axios.get('api/data-freshness/contracts/', {
          params: { node: this.nodeKey },
        });
        if (!resp.data?.supported) return;
        this.contracts = resp.data?.contracts ?? [];
        this.contractCount = resp.data?.contract_count ?? 0;
      } catch (e) {
        this.contracts = [];
        this.contractCount = 0;
      }
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
  },
};
</script>
